import { useState } from "react";
import { isoDay, isoWeek, leaveOnDay, LEAVE_LABEL } from "./dateHelpers";
import LeaveEditDialog from "@/components/LeaveEditDialog";
import { Airplane } from "@phosphor-icons/react";
import { CATEGORIES } from "@/components/InterventionCategoryPicker";

/**
 * Compact month grid with 3 mini-blocks per day + overflow counter.
 * Approved leaves are painted as a small green pill (initials + count) so
 * the admin gets a bird's-eye view of who is off in the month.
 * Click a day → onDayClick(d) → parent typically zooms to week view for that date.
 */
export default function MonthView({ days, anchor, interventions, team, chantiersMap, onDayClick, leaves = [], isAdmin = false, onChange }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const monthIdx = anchor.getMonth();
  const teamById = Object.fromEntries(team.map((u) => [u.id, u]));
  const weekdayHeads = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
  const [editingLeave, setEditingLeave] = useState(null);

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden shadow-lg shadow-black/20" data-testid="month-view">
      <div className="grid border-b border-white/10" style={{ gridTemplateColumns: "36px repeat(7, minmax(0, 1fr))" }}>
        <div className="p-2 text-center text-[9px] uppercase tracking-widest text-white/30">SEM.</div>
        {weekdayHeads.map((w) => (
          <div key={w} className="p-2 text-center text-[10px] uppercase tracking-widest text-white/40 border-l border-white/5">{w}</div>
        ))}
      </div>
      <div>
        {Array.from({ length: 6 }).map((_, weekIdx) => {
          const weekDays = days.slice(weekIdx * 7, weekIdx * 7 + 7);
          if (weekDays.length === 0) return null;
          const wNum = isoWeek(weekDays[0]);
          return (
            <div key={`${weekDays[0].toISOString()}-w${wNum}`} className="grid" style={{ gridTemplateColumns: "36px repeat(7, minmax(0, 1fr))" }}>
              <div className="p-2 text-center text-[10px] font-mono tabular-nums text-white/40 border-t border-white/5 flex items-center justify-center">
                S{wNum}
              </div>
              {weekDays.map((d) => {
                const iso = isoDay(d);
                const inMonth = d.getMonth() === monthIdx;
                const isToday = d.getTime() === today.getTime();
                const iv = interventions.filter((x) => (x.scheduled_date || "").slice(0, 10) === iso);
                // Approved leaves overlapping this day
                const dayLeaves = team
                  .map((u) => leaveOnDay(u.id, iso, leaves))
                  .filter(Boolean);
                const hasLeave = dayLeaves.length > 0;
                return (
                  <div
                    key={iso}
                    className={`relative border-l border-t border-white/5 min-h-[80px] md:min-h-[100px] transition-all ${inMonth ? "" : "opacity-30"} ${isToday ? "bg-[#005CFF]/10" : ""} ${hasLeave ? "bg-emerald-500/[0.06]" : ""}`}
                    data-testid={`month-day-${iso}`}
                  >
                    <button
                      onClick={() => onDayClick(d)}
                      className="absolute inset-0 text-left p-1.5 space-y-1 hover:bg-white/[0.04] active:scale-[0.98] transition"
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-mono tabular-nums ${isToday ? "text-[#005CFF] font-bold" : "text-white/60"}`}>{d.getDate()}</span>
                        {iv.length > 0 && (
                          <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-full ${isToday ? "bg-[#005CFF]/30 text-white" : "bg-white/10 text-white/60"}`}>{iv.length}</span>
                        )}
                      </div>
                      <div className="space-y-0.5">
                        {iv.slice(0, 3).map((x) => {
                          const u = teamById[x.technician_id];
                          const ch = chantiersMap?.[x.chantier_id];
                          const c = x.color || ch?.color || u?.color || "#005CFF";
                          const cat = x.categorie && CATEGORIES[x.categorie];
                          return (
                            <div key={x.id} className="text-[9px] md:text-[10px] rounded px-1 py-0.5 truncate flex items-center gap-1" style={{ background: c + "22", borderLeft: `2px solid ${c}` }}>
                              {cat && <span aria-hidden style={{ color: cat.color }}>●</span>}
                              <span className="opacity-70 font-mono">{new Date(x.scheduled_date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                              <span className="truncate">{x.title}</span>
                            </div>
                          );
                        })}
                        {iv.length > 3 && <div className="text-[9px] text-white/40">+ {iv.length - 3}</div>}
                      </div>
                    </button>

                    {hasLeave && (
                      <div className="absolute bottom-1 left-1 right-1 flex flex-wrap gap-0.5 z-10 pointer-events-none">
                        {dayLeaves.slice(0, 4).map((l) => {
                          const uu = teamById[l.user_id];
                          const initials = (uu?.name || l.user_name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
                          return (
                            <button
                              key={l.id}
                              onClick={(e) => { e.stopPropagation(); if (isAdmin) setEditingLeave(l); }}
                              disabled={!isAdmin}
                              className={`pointer-events-auto flex items-center gap-0.5 rounded-full px-1 py-[1px] text-[8px] font-semibold border border-emerald-500/40 bg-emerald-500/20 text-emerald-200 ${isAdmin ? "hover:bg-emerald-500/35 hover:scale-110 active:scale-95" : "cursor-default"} transition`}
                              title={`${LEAVE_LABEL[l.type] || "Congé"} · ${uu?.name || l.user_name}${l.reason ? " · " + l.reason : ""}`}
                              data-testid={`month-leave-${l.id}-${iso}`}
                            >
                              <Airplane size={7} weight="fill" />
                              {initials}
                            </button>
                          );
                        })}
                        {dayLeaves.length > 4 && (
                          <span className="text-[8px] font-mono text-emerald-300/60 pointer-events-auto">+{dayLeaves.length - 4}</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <LeaveEditDialog
        open={!!editingLeave}
        onOpenChange={(o) => !o && setEditingLeave(null)}
        leave={editingLeave}
        onChanged={onChange}
      />
    </div>
  );
}
